// =====================================================================
// CookieBanner, one-shot opt-in / opt-out card for analytics cookies.
// • Renders only when the user has not made a choice yet (no
//   `lm-cookie-consent` in localStorage).
// • Accept → opt the visitor into PostHog and dismiss forever.
// • Decline → record the decline and never show again. Posthog stays
//   opted out.
// =====================================================================

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAnalytics } from "../context/AnalyticsContext.jsx";

const KEY = "lm-cookie-consent";

// Temporarily hide the consent popup WITHOUT removing any of the cookie /
// PostHog machinery. While this is false the banner never renders; and because
// posthog.js only opts in on a stored "accepted" decision, analytics stays
// opted-out, so nothing is captured without consent (no legal exposure while
// the popup is hidden). Flip back to true to bring the banner back.
const BANNER_ENABLED = false;

export default function CookieBanner() {
  const { optIn, optOut, capture } = useAnalytics();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!BANNER_ENABLED) return;
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(KEY);
    if (!stored) setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = () => {
    localStorage.setItem(KEY, "accepted");
    optIn();
    capture("Cookie Consent", { decision: "accepted" });
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem(KEY, "declined");
    optOut();
    setVisible(false);
  };

  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="Cookie consent">
      <div className="cookie-copy">
        <strong>Cookies for analytics</strong>
        <p>
          We use a single first-party cookie to measure how the product
          is used. No personally-identifying tracking by default. See our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>
      </div>
      <div className="cookie-actions">
        <button type="button" className="cookie-decline" onClick={decline}>
          Decline
        </button>
        <button type="button" className="cookie-accept" onClick={accept}>
          Accept
        </button>
      </div>
    </div>
  );
}
